import React from 'react';
import { CardStats } from '@/components';
import { DashboardResource } from '@/hooks/useDashboardResource';
import {
  RentalDashboardSummary,
  RentalStats,
  RentalStatsWithArrears,
} from '@/types/rental';
import { formatRupiah } from '@/utils/format';
import {
  formatCollectionRate,
  formatStuckAge,
  isBlockFailed,
  resourceCardProps,
} from '../dashboardDisplay';

interface FleetCardsProps {
  stats: DashboardResource<RentalStatsWithArrears>;
  summary: DashboardResource<RentalDashboardSummary>;
  onRetryStats: () => void;
  onRetrySummary: () => void;
  onOpenTab: (tab: keyof RentalStats) => void;
}

/**
 * The two unwindowed blocks: what the fleet looks like right now, and what
 * needs a human today.
 *
 * Neither moves when the date filter moves, which is why they sit outside the
 * Performa block rather than in one flat grid with it. Overdue overlaps Active
 * and Waiting Return (it is a derived flag, not a status), so these numbers
 * must never be summed into a total.
 *
 * No deltas here. The daily snapshot that would have supplied them was
 * cancelled: the scheduling columns behind it are 9% and 2% populated with six
 * days of history, and a "trend" over a figure that is zero on most days is
 * noise wearing a chart.
 */
export const FleetCards: React.FC<FleetCardsProps> = ({
  stats,
  summary,
  onRetryStats,
  onRetrySummary,
  onOpenTab,
}) => {
  const counts = stats.data;
  // Asked the same way as the summary blocks below. These two used to differ —
  // raw `status === 'failed'` here, isBlockFailed there — which is exactly the
  // drift one helper prevents.
  const countProps = resourceCardProps(stats, counts, onRetryStats);

  const health = summary.data?.rentalHealth ?? null;
  const arrears = summary.data?.arrears ?? null;
  const repoStuck = summary.data?.repoStuck ?? null;


  return (
    <>
      <section aria-labelledby="armada-heading" className="mb-6">
        <h2
          id="armada-heading"
          className="mb-3 text-[15px] font-semibold text-gray-900"
        >
          Kondisi Armada
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <CardStats
            size="small"
            title="Active"
            value={counts?.active ?? '-'}
            icon="ri-e-bike-2-line"
            color="blue"
            {...countProps}
            subtitle="unit di mitra"
            onClick={() => onOpenTab('active')}
          />
          <CardStats
            size="small"
            title="Waiting Handover"
            value={counts?.waitingHandover ?? '-'}
            icon="ri-hand-coin-line"
            color="purple"
            {...countProps}
            onClick={() => onOpenTab('waitingHandover')}
          />
          <CardStats
            size="small"
            title="Waiting Return"
            value={counts?.waitingReturn ?? '-'}
            icon="ri-arrow-go-back-line"
            color="yellow"
            {...countProps}
            onClick={() => onOpenTab('waitingReturn')}
          />
          <CardStats
            size="small"
            title="Kesehatan Sewa"
            value={health ? formatCollectionRate(health.rate) : '-'}
            icon="ri-heart-pulse-line"
            color="green"
            {...resourceCardProps(summary, health, onRetrySummary)}
            subtitle={
              health
                ? `${health.lateRentalCount} dari ${health.totalRentalCount} unit telat ≥ ${health.thresholdDays} hari`
                : undefined
            }
          />
        </div>
      </section>

      <section aria-labelledby="action-heading" className="mb-6">
        <h2
          id="action-heading"
          className="mb-3 text-[15px] font-semibold text-gray-900"
        >
          Butuh Action
        </h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <CardStats
            size="small"
            title="Overdue"
            value={counts?.overdue ?? '-'}
            icon="ri-alarm-warning-line"
            color="red"
            {...countProps}
            // The count comes from getRentalStats and the money from the
            // summary's arrears leg. They fail independently, so the subtitle
            // names the gap instead of vanishing and leaving the card looking
            // complete.
            subtitle={
              arrears
                ? `Tunggakan ${formatRupiah(arrears.totalAmount)}`
                : isBlockFailed(summary, arrears)
                  ? 'Tunggakan gagal dimuat'
                  : undefined
            }
            onClick={() => onOpenTab('overdue')}
          />
          <CardStats
            size="small"
            title="Repo Tertahan"
            value={repoStuck ? repoStuck.count : '-'}
            icon="ri-truck-line"
            color="red"
            {...resourceCardProps(summary, repoStuck, onRetrySummary)}
            subtitle={
              repoStuck ? formatStuckAge(repoStuck.oldestDays) : undefined
            }
          />
        </div>
      </section>
    </>
  );
};

export default FleetCards;
